// /connect?code=XXXX-YYYY — Device authorization grant approval page.
//
// Reached by the SDK's Client.login() printing the login_url to the
// terminal. RequireAuth wraps the route, so unauthenticated users
// bounce to /auth/login?redirect=/connect?code=… and land back here
// after sign-in.
//
// Flow:
//   1. Read `code` query param.
//   2. GET /auth/device/lookup → {client_name, status, expires_in}.
//   3. If status=pending: render [Approve][Deny] buttons.
//   4. Approve → POST /approve → show "Connected ✓ close this tab".
//   5. Deny    → POST /deny    → show "Denied".
//   6. status ∈ {approved, consumed, denied, expired} on first lookup:
//      render the terminal state, no buttons.

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { mmss } from '../../../packages/views/time';
import Icon from '../../../packages/ui/Icon';

interface LookupResponse {
  client_name: string;
  status: 'pending' | 'approved' | 'consumed' | 'denied' | 'expired';
  expires_in: number;
}

const BRAND = {
  cream:  '#fefcf6',
  paper:  '#ffffff',
  navy:   '#234976',
  navyD:  '#1a3554',
  tan:    '#d9b47b',
  border: 'rgba(35,73,118,0.14)',
  ink1:   '#4a6585',
  inkMute:'#82929f',
  good:   '#2d8a3e',
  err:    '#c72d24',
  errBg:  'rgba(199,45,36,0.10)',
};

export function Connect() {
  const t = useT();
  const [params] = useSearchParams();
  const code = params.get('code') || '';

  const { data, isLoading, error, refetch } = useQuery<LookupResponse>({
    queryKey: ['device_lookup', code],
    queryFn: () => api<LookupResponse>(`/api/v2/auth/device/lookup`, { query: { code } }),
    enabled: !!code,
    retry: false,
  });

  // Countdown for the expires_in field — refetch every 15s so the
  // ticker stays roughly correct while the user reads the page.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  useEffect(() => {
    if (data?.expires_in != null) setSecondsLeft(data.expires_in);
  }, [data?.expires_in]);
  useEffect(() => {
    if (secondsLeft == null || secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft(s => (s == null ? null : Math.max(0, s - 1))), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  const approveMut = useMutation({
    mutationFn: () => api(`/api/v2/auth/device/approve`, {
      method: 'POST', body: { user_code: code },
    }),
    onSuccess: () => refetch(),
  });
  const denyMut = useMutation({
    mutationFn: () => api(`/api/v2/auth/device/deny`, {
      method: 'POST', body: { user_code: code },
    }),
    onSuccess: () => refetch(),
  });

  if (!code) return <Wrap><Headline>{t('connect_page.no_code_h')}</Headline><Body>{t('connect_page.no_code_body_a')}<code>?code=…</code>{t('connect_page.no_code_body_b')}</Body></Wrap>;
  if (isLoading) return <Wrap><Headline>{t('connect_page.loading_h')}</Headline></Wrap>;
  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    if (status === 404) return <Wrap><Headline tone="err">{t('connect_page.not_found_h')}</Headline><Body>{t('connect_page.not_found_body')}</Body></Wrap>;
    return <Wrap><Headline tone="err">{t('connect_page.load_failed_h')}</Headline><Body>{String((error as Error).message)}</Body></Wrap>;
  }
  if (!data) return null;

  // Was approved + already consumed by polling client (api_key delivered).
  if (data.status === 'consumed') return <Wrap><Headline tone="good"><Icon name="check" size={17} /> {t('connect_page.consumed_h')}</Headline><Body>{t('connect_page.consumed_body_a')}<Link to="/account" style={linkSty}>{t('connect_page.consumed_body_link')}</Link>{t('connect_page.consumed_body_b')}</Body></Wrap>;
  // Approved but client hasn't polled yet — same UX outcome.
  if (data.status === 'approved') return <Wrap><Headline tone="good"><Icon name="check" size={17} /> {t('connect_page.approved_h')}</Headline><Body>{t('connect_page.approved_body')}</Body></Wrap>;
  if (data.status === 'denied') return <Wrap><Headline tone="err">{t('connect_page.denied_h')}</Headline><Body>{t('connect_page.denied_body')}</Body></Wrap>;
  if (data.status === 'expired') return <Wrap><Headline tone="err">{t('connect_page.expired_h')}</Headline><Body>{t('connect_page.expired_body')}</Body></Wrap>;

  // status === 'pending' — render the [Approve][Deny] UI.
  const cd = secondsLeft ?? data.expires_in;

  return (
    <Wrap>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 5, color: BRAND.tan, textTransform: 'uppercase', marginBottom: 6 }}>
        {t('connect_page.eyebrow')}
      </div>
      <div style={{
        margin: '0 0 14px', padding: '8px 12px', borderRadius: 8,
        background: 'rgba(217,180,123,0.15)', border: `1px solid ${BRAND.tan}`,
        fontSize: 13, color: BRAND.ink1, lineHeight: 1.55,
      }}>
        {t('connect_page.deprecated_a')}{' '}
        <Link to="/mcp" style={linkSty}>{t('connect_page.deprecated_link')}</Link>{t('connect_page.deprecated_b')}
      </div>
      <Headline>
        <span style={{
          backgroundImage: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.tan} 100%)`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
        }}>{data.client_name}</span>
        <span style={{ color: BRAND.navy }}>{t('connect_page.wants_connect')}</span>
      </Headline>
      <Body>
        <p style={{ margin: '0 0 10px' }}>{t('connect_page.grant_intro_a')}<code>al_live_…</code>{t('connect_page.grant_intro_b')}</p>
        <ul style={{ margin: '8px 0 14px 18px', padding: 0, lineHeight: 1.7, color: BRAND.ink1 }}>
          <li>{t('connect_page.grant_li1')}(<b>{t('connect_page.grant_li1_warn')}</b>)</li>
          <li>{t('connect_page.grant_li2')}</li>
          <li><b>{t('connect_page.grant_li3_pre')}</b>{t('connect_page.grant_li3')}</li>
        </ul>
        <p style={{ margin: '6px 0', color: BRAND.inkMute, fontSize: 13 }}>
          {t('connect_page.revoke_a')}<Link to="/account" style={linkSty}>{t('connect_page.revoke_link')}</Link>{t('connect_page.revoke_b')}<code>{code}</code>   ·   <b>{mmss(cd)}</b>{t('connect_page.expires_in')}
        </p>
      </Body>

      <div style={{ display: 'flex', gap: 12, marginTop: 22, flexWrap: 'wrap' }}>
        <button
          onClick={() => approveMut.mutate()}
          disabled={approveMut.isPending}
          style={btnPrimary}
        >
          {approveMut.isPending ? t('connect_page.approving') : t('connect_page.approve')}
        </button>
        <button
          onClick={() => denyMut.mutate()}
          disabled={denyMut.isPending}
          style={btnSecondary}
        >
          {denyMut.isPending ? t('connect_page.denying') : t('connect_page.deny')}
        </button>
      </div>

      {(approveMut.error || denyMut.error) && (
        <div style={{
          marginTop: 18, padding: '10px 14px', borderRadius: 8,
          background: BRAND.errBg, color: BRAND.err, fontSize: 13,
        }}>
          {String((approveMut.error || denyMut.error) as Error)}
        </div>
      )}
    </Wrap>
  );
}

// ─── Layout / styled bits ──────────────────────────────────────

function Wrap({ children }: { children: ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', background: BRAND.cream,
      fontFamily: '"Inter", "Noto Sans SC", system-ui, "PingFang SC", sans-serif',
      color: BRAND.navy, padding: '64px 24px',
      display: 'flex', justifyContent: 'center',
    }}>
      <div style={{
        width: '100%', maxWidth: 560,
        background: BRAND.paper, border: `1px solid ${BRAND.border}`,
        borderRadius: 14, padding: '32px 36px',
        boxShadow: '0 16px 50px rgba(35,73,118,0.10)',
      }}>
        {children}
      </div>
    </div>
  );
}

function Headline({ children, tone }: { children: ReactNode; tone?: 'good' | 'err' }) {
  const color = tone === 'good' ? BRAND.good : tone === 'err' ? BRAND.err : BRAND.navy;
  return (
    <h1 style={{
      fontSize: 32, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.2,
      margin: '0 0 14px', color,
    }}>{children}</h1>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 15, color: BRAND.ink1, lineHeight: 1.6 }}>{children}</div>;
}

const linkSty: CSSProperties = {
  color: BRAND.navy, textDecoration: 'underline', textUnderlineOffset: 2,
};

const btnBase: CSSProperties = {
  fontFamily: 'inherit', fontSize: 15, fontWeight: 700,
  padding: '11px 22px', borderRadius: 8, cursor: 'pointer',
  border: '1px solid transparent', transition: 'background .12s, color .12s',
};
const btnPrimary: CSSProperties = {
  ...btnBase, background: BRAND.navy, color: '#ffffff', borderColor: BRAND.navy,
};
const btnSecondary: CSSProperties = {
  ...btnBase, background: 'transparent', color: BRAND.ink1, borderColor: BRAND.border,
};
